"use client"

import { useState } from "react"
import { ChevronLeft, GraduationCap, Lock, X } from "lucide-react"

import { Dialog, DialogContent, DialogTitle } from "@/common/components/ui/dialog"
import { cn } from "@/common/lib/utils"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import LmsCatalog from "./lms-catalog"
import LmsCourseDetail from "./lms-course-detail"
import LmsPlayer from "./lms-player"
import LmsTranscript from "./lms-transcript"

/**
 * The simulated LMS — a sixth surface, and deliberately a DIFFERENT PRODUCT.
 *
 * Everything else in this demo carries Compass's own accent and wordmark.
 * This does not: no Compass branding anywhere inside the frame, its own
 * (fictional) host in the chrome bar, its own layout language. Compass claims
 * to integrate with any LMS — that claim dies the moment this looks like it
 * built one. `docs/lms-integration.md` is the research this dresses up.
 *
 * Two ways in, and they behave differently on purpose:
 *  - "Hier starten" on a curriculum item is an LTI deep-link. A real one lands
 *    a learner directly in the course, not in a catalogue they have to
 *    search — so `initialBausteinId` skips straight to the player.
 *  - The Integrationen tile is "go look at the whole system Compass reaches
 *    into", so it opens at the catalogue root.
 */

type Screen =
    | { kind: "catalog" }
    | { kind: "detail"; bausteinId: string }
    | { kind: "player"; bausteinId: string }
    | { kind: "transcript" }

export interface LmsShellHandle {
    initialBausteinId: string | null
}

export default function LmsShell({
    state,
    shell,
}: {
    state: LernpfadDemoState
    shell: LmsShellHandle
}) {
    const { localizer, closeLms } = state
    const { t } = localizer

    const [screen, setScreen] = useState<Screen>(() =>
        shell.initialBausteinId
            ? { kind: "player", bausteinId: shell.initialBausteinId }
            : { kind: "catalog" },
    )

    const showTabs = screen.kind !== "player"
    const activeTab = screen.kind === "transcript" ? "transcript" : "catalog"

    return (
        <Dialog open onOpenChange={(isOpen) => !isOpen && closeLms()}>
            <DialogContent
                // Radix portals this onto `document.body`, outside the
                // `[data-lp-charts]` wrapper that carries the demo's typeface
                // and colour tokens. `chart-theme.tsx` repeats its rule for this
                // attribute so the portalled surface is themed too — without it
                // the LMS renders in the host app's font with `currentColor`
                // borders, and only in the standalone build does a body-level
                // font rule accidentally cover for it.
                data-lp-portal
                className="flex h-[85vh] max-w-5xl flex-col gap-0 overflow-hidden p-0"
                aria-describedby={undefined}
                showCloseButton={false}
            >
                <DialogTitle className="sr-only">{t("lms.shell.title")}</DialogTitle>

                {/* Faux browser chrome — the one visual cue that matters: this is
                    NOT Compass, it is the system Compass launched into. The default
                    Dialog close button is turned off (it assumes the content's
                    default padding, and this chrome bar sits flush at `p-0`) in
                    favour of a close control built into the chrome bar itself —
                    which also reads more like an actual browser window. */}
                <div className="flex shrink-0 items-center gap-2 border-b bg-muted/60 px-4 py-2.5">
                    <span className="flex gap-1.5">
                        <span className="size-2.5 rounded-full bg-muted-foreground/30" />
                        <span className="size-2.5 rounded-full bg-muted-foreground/30" />
                        <span className="size-2.5 rounded-full bg-muted-foreground/30" />
                    </span>
                    <span className="ml-2 inline-flex items-center gap-1.5 rounded-md bg-background px-2.5 py-1 text-xs text-muted-foreground">
                        <Lock className="size-3" />
                        {t("player.chrome")}
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <GraduationCap className="size-3.5" />
                        {t("lms.shell.title")}
                    </span>
                    <button
                        type="button"
                        onClick={closeLms}
                        aria-label={t("lms.shell.close")}
                        className="ml-1 shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                    >
                        <X className="size-4" />
                    </button>
                </div>

                {showTabs && (
                    <div className="flex shrink-0 gap-1 border-b bg-background px-4 pt-2">
                        {(["catalog", "transcript"] as const).map((tab) => (
                            <button
                                key={tab}
                                type="button"
                                onClick={() =>
                                    setScreen(tab === "catalog" ? { kind: "catalog" } : { kind: "transcript" })
                                }
                                className={cn(
                                    "-mb-px rounded-t-md border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                                    activeTab === tab
                                        ? "border-foreground text-foreground"
                                        : "border-transparent text-muted-foreground hover:text-foreground",
                                )}
                            >
                                {t(tab === "catalog" ? "lms.shell.tab.catalog" : "lms.shell.tab.transcript")}
                            </button>
                        ))}
                    </div>
                )}

                <div className="min-h-0 flex-1 overflow-y-auto">
                    {screen.kind === "catalog" && (
                        <LmsCatalog
                            state={state}
                            onSelect={(bausteinId) => setScreen({ kind: "detail", bausteinId })}
                        />
                    )}

                    {screen.kind === "detail" && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setScreen({ kind: "catalog" })}
                                className="m-4 mb-0 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                            >
                                <ChevronLeft className="size-4" />
                                {t("lms.shell.back")}
                            </button>
                            <LmsCourseDetail
                                state={state}
                                bausteinId={screen.bausteinId}
                                onStart={(bausteinId) => setScreen({ kind: "player", bausteinId })}
                            />
                        </div>
                    )}

                    {screen.kind === "player" && (
                        <LmsPlayer
                            state={state}
                            bausteinId={screen.bausteinId}
                            onDone={closeLms}
                        />
                    )}

                    {screen.kind === "transcript" && <LmsTranscript state={state} />}
                </div>
            </DialogContent>
        </Dialog>
    )
}
